import { ApiService, type ApiRequestConfig } from "./api.service";
import { normalizeIntakeDefinition } from "@/lib/intake/normalize-definition";
import type { TIntakePage } from "@/types/intake";
import type {
  TFormWorkflow,
  TWorkflowAvailableField,
  TWorkflowDraftSavePayload,
  TWorkflowLookups,
  TWorkflowPreviewAdvancePayload,
  TWorkflowPreviewStep,
  TWorkflowStatusSource,
} from "@/types/form-workflow";

/**
 * Form Workflow Builder draft API (Epic #2086, persistence #2116). Hangs off an
 * existing intake form. Server-side access is verb-level, not admin-only:
 * ``form_workflow.read`` opens the builder to an admin, the owning team's lead, and
 * (since NG-409) a view-only viewer/editor of an owning team; ``.edit`` / ``.publish``
 * stay with the first two, so every write below 403s for a reader.
 * Mirrors ``request-workflow.service.ts``: a thin ``ApiService`` wrapper that
 * builds the URL and lets axios errors propagate so the store can log them with
 * context (this layer never catches).
 *
 * The save is a whole-document replace (PUT) — every call is a complete,
 * idempotent snapshot, which is what makes it tolerant of frequent autosaves.
 */
export class FormWorkflowService extends ApiService {
  private basePath(slug: string, formId: string): string {
    return `/api/workspaces/${slug}/intake/forms/${formId}/workflow`;
  }

  /** Same definition boundary the requester paths use (#3760): a Preview step
   *  carries a reconstructed intake ``definition`` that ``forms-edit`` reads as
   *  ``previewStep.definition.pages``, so coerce it here rather than leave the
   *  builder one malformed snapshot away from a white screen.
   *
   *  ``may-be-empty``: this walks the author's LIVE *draft* graph, which
   *  legitimately has nothing in it until they've built something — alarming on
   *  that would fire every time someone previews a new form. Structural
   *  corruption still reports; the crash is prevented either way. */
  private normalizePreviewStep(
    slug: string,
    formId: string,
    source: string,
    step: TWorkflowPreviewStep,
  ): TWorkflowPreviewStep {
    const definition = normalizeIntakeDefinition(step.definition, {
      source,
      expectation: "may-be-empty",
      workspaceSlug: slug,
      formId,
    });
    return definition === step.definition ? step : { ...step, definition };
  }

  /** GET the form's draft workflow. Never 404s for a real form: the BE
   *  get-or-creates + seeds the fixed first Page on first access for a caller who may
   *  EDIT it, and serves a reader the not-yet-created envelope (`id: null`,
   *  `elements: []`) rather than authoring the graph they came to look at (NG-409). */
  async loadWorkflow(
    slug: string,
    formId: string,
    config?: ApiRequestConfig,
  ): Promise<TFormWorkflow> {
    return this.get(`${this.basePath(slug, formId)}/`, config);
  }

  /** PUT the whole document (autosave). ``elements`` omitted ⇒ name-only save. */
  async saveWorkflow(
    slug: string,
    formId: string,
    body: TWorkflowDraftSavePayload,
  ): Promise<TFormWorkflow> {
    return this.put(`${this.basePath(slug, formId)}/`, body);
  }

  /** GET one Page's current elements reconstructed as an intake definition page —
   *  what the Edit Page modal hydrates its tree from on open (#2271). */
  async loadPageDefinition(
    slug: string,
    formId: string,
    pageId: string,
  ): Promise<{ page: TIntakePage }> {
    return this.get(`${this.basePath(slug, formId)}/pages/${pageId}/definition/`);
  }

  /** PUT the edited page as an intake definition page; the BE converts it via the
   *  shared #2202 cutover converter and replaces the Page's element rows, then
   *  returns the whole workflow envelope. Throws (400 carrying ``blockers``) when
   *  the page has content the normalized model can't represent (#2271). */
  async savePageDefinition(
    slug: string,
    formId: string,
    pageId: string,
    page: TIntakePage,
  ): Promise<TFormWorkflow> {
    return this.put(`${this.basePath(slug, formId)}/pages/${pageId}/definition/`, { page });
  }

  /** GET the first step of a builder Preview walk of the LIVE draft graph
   *  (#2551), so the preview reflects what the author is building pre-publish
   *  rather than the legacy blob. Self-guards with ``kind: null`` when the draft
   *  has no Page step yet; never seeds a workflow. Not LaunchDarkly-gated
   *  server-side (#2503). ``startElementId`` starts the walk at that Page
   *  instead of the fixed first one (#2590); the returned step's ``partial``
   *  flag reports whether upstream Branch/Review state was skipped. */
  async loadPreview(
    slug: string,
    formId: string,
    startElementId?: string,
  ): Promise<TWorkflowPreviewStep> {
    const query = startElementId ? `?start_element_id=${encodeURIComponent(startElementId)}` : "";
    const step = await this.get<TWorkflowPreviewStep>(
      `${this.basePath(slug, formId)}/preview/${query}`,
    );
    return this.normalizePreviewStep(slug, formId, "loadPreview", step);
  }

  /** POST to advance one step of the builder Preview walk (#2551) — evaluating
   *  any intervening Branch condition / Review decision live off the LIVE draft
   *  graph and returning the next step. Side-effect-free; nothing persists. */
  async advancePreview(
    slug: string,
    formId: string,
    body: TWorkflowPreviewAdvancePayload,
  ): Promise<TWorkflowPreviewStep> {
    const step = await this.post<TWorkflowPreviewStep>(
      `${this.basePath(slug, formId)}/preview/advance/`,
      body,
    );
    return this.normalizePreviewStep(slug, formId, "advancePreview", step);
  }

  /** GET the config-panel pick lists (teams / assignable users / statuses). */
  async loadLookups(
    slug: string,
    formId: string,
    config?: ApiRequestConfig,
  ): Promise<TWorkflowLookups> {
    return this.get(`${this.basePath(slug, formId)}/lookups/`, config);
  }

  /** GET the bank fields that precede an element on the graph — the valid
   *  condition sources for a Branch (M4). */
  async loadAvailableFields(
    slug: string,
    formId: string,
    elementId: string,
  ): Promise<{ fields: TWorkflowAvailableField[] }> {
    return this.get(`${this.basePath(slug, formId)}/elements/${elementId}/available-fields/`);
  }

  /** GET whether a Page's Status is derived from an upstream Review branch.
   *  A Page that opens a Review decision-option lane inherits that option's
   *  status; the BE computes the graph-aware signal so the FE can pre-fill +
   *  disable the Status control rather than walking the canvas (#2406). */
  async loadStatusSource(
    slug: string,
    formId: string,
    elementId: string,
  ): Promise<TWorkflowStatusSource> {
    return this.get(`${this.basePath(slug, formId)}/elements/${elementId}/status-source/`);
  }

  /** POST to publish the draft. Resolves with the published workflow on 200;
   *  rejects with a 422 carrying ``incomplete_nodes`` when paths are incomplete. */
  async publish(slug: string, formId: string): Promise<TFormWorkflow> {
    return this.post(`${this.basePath(slug, formId)}/publish/`, {});
  }

  /** POST to throw the unpublished draft graph away and restore the last
   *  published one (NG-211). Resolves with the reverted workflow on 200; rejects
   *  with a 409 when the workflow has never been published, because then there
   *  is no version to go back to. */
  async discard(slug: string, formId: string): Promise<TFormWorkflow> {
    return this.post(`${this.basePath(slug, formId)}/discard/`, {});
  }

  /** POST to start a NEVER-PUBLISHED workflow over from the seeded skeleton
   *  (NG-235). The sibling of {@link discard}, not a variant: discard restores a
   *  published version, this one destroys work with nothing to fall back on.
   *  Rejects with a 409 on a published workflow, which wants discard instead. */
  async reset(slug: string, formId: string): Promise<TFormWorkflow> {
    return this.post(`${this.basePath(slug, formId)}/reset/`, {});
  }
}
